import { RefreshCw } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { EncodingSelect } from "@/components/crypto/EncodingSelect";
import {
  BYTE_ENCODINGS,
  type ByteEncoding,
  randomEncoded,
  safeByteLength,
} from "@/lib/crypto";

/**
 * The Key + IV input pair, shared by both the encryption and decryption
 * forms. Reads the surrounding react-hook-form context, so it must be
 * rendered inside a `<Form>` whose schema has `key`, `keyEncoding`, `iv`,
 * and `ivEncoding` fields.
 */
export function KeyIvFields() {
  const form = useFormContext();

  const keyEncoding = form.watch("keyEncoding") as ByteEncoding;
  const ivEncoding = form.watch("ivEncoding") as ByteEncoding;
  const keyLen = safeByteLength(form.watch("key") ?? "", keyEncoding);
  const ivLen = safeByteLength(form.watch("iv") ?? "", ivEncoding);

  function generateKey() {
    form.setValue("keyEncoding", "hex");
    form.setValue("key", randomEncoded(32, "hex"), { shouldValidate: true });
  }

  function generateIv() {
    form.setValue("ivEncoding", "hex");
    form.setValue("iv", randomEncoded(16, "hex"), { shouldValidate: true });
  }

  return (
    <>
      <FormField
        control={form.control}
        name="key"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Encryption key</FormLabel>
            <div className="flex gap-2">
              <FormControl>
                <Input placeholder="Secret key" {...field} />
              </FormControl>
              <EncodingSelect
                ariaLabel="Key encoding"
                value={keyEncoding}
                onValueChange={(v) =>
                  form.setValue("keyEncoding", v as ByteEncoding, {
                    shouldValidate: true,
                  })
                }
                options={BYTE_ENCODINGS}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Generate a random 256-bit key"
                onClick={generateKey}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <FormDescription>
              {keyLen === null
                ? "Invalid for the selected encoding."
                : `${keyLen} byte${keyLen === 1 ? "" : "s"} — valid sizes: 16 / 24 / 32`}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="iv"
        render={({ field }) => (
          <FormItem>
            <FormLabel>IV (initialization vector)</FormLabel>
            <div className="flex gap-2">
              <FormControl>
                <Input placeholder="16-byte IV" {...field} />
              </FormControl>
              <EncodingSelect
                ariaLabel="IV encoding"
                value={ivEncoding}
                onValueChange={(v) =>
                  form.setValue("ivEncoding", v as ByteEncoding, {
                    shouldValidate: true,
                  })
                }
                options={BYTE_ENCODINGS}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Generate a random 16-byte IV"
                onClick={generateIv}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <FormDescription>
              {ivLen === null
                ? "Invalid for the selected encoding."
                : `${ivLen} byte${ivLen === 1 ? "" : "s"} — must be 16`}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}
